import { supabase } from './supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './constants';

const IMPERSONATION_KEY = 'honeydone_impersonation_state';
const ADMIN_SESSION_KEY = 'honeydone_admin_session';

export interface ImpersonationState {
  isImpersonating: boolean;
  originalAdminId: string;
  impersonatedUserId: string;
  impersonatedUserName: string;
  logId: string;
}

/**
 * Safely get an item from sessionStorage, falling back to localStorage
 */
const safeGetSessionStorage = (key: string): string | null => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem(key);
    }
  } catch (error) {
    // sessionStorage not available, fall through to localStorage
  }
  return null;
};

/**
 * Safely set an item in sessionStorage, falling back to localStorage
 */
const safeSetSessionStorage = (key: string, value: string): void => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, value);
      return;
    }
  } catch (error) {
    // sessionStorage not available, fall through to localStorage
  }
  // Fallback to localStorage if sessionStorage is not available
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(key, value);
  }
};

/**
 * Safely remove an item from sessionStorage
 */
const safeRemoveSessionStorage = (key: string): void => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    // sessionStorage not available, ignore
  }
};

/**
 * Get the current impersonation state from localStorage
 */
export const getImpersonationState = (): ImpersonationState | null => {
  try {
    // Check sessionStorage first (for new tab-based impersonation)
    const stored = safeGetSessionStorage(IMPERSONATION_KEY) || (typeof localStorage !== 'undefined' ? localStorage.getItem(IMPERSONATION_KEY) : null);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error reading impersonation state:', error);
    return null;
  }
};

/**
 * Start impersonating a user
 * @param userId - The ID of the user to impersonate
 * @returns Promise resolving to the impersonated user's data
 */
export const startImpersonation = async (userId: string): Promise<void> => {
  try {
    // Get current session (admin session)
    const { data: { session: adminSession }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !adminSession) {
      throw new Error('No active admin session');
    }

    // Store the admin session
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(adminSession));
    }

    // Call the edge function to generate impersonation tokens
    // Using direct fetch for better error handling and CORS compatibility
    const functionUrl = `${SUPABASE_URL}/functions/v1/impersonate-user`;
    console.log('Calling impersonation function:', functionUrl);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminSession.access_token}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Function response error:', errorText);
      throw new Error(`Failed to start impersonation: ${response.status} ${errorText}`);
    }

    const functionData = await response.json();

    if (!functionData || functionData.error) {
      throw new Error(functionData?.error || 'Failed to start impersonation');
    }

    const { magic_link, impersonated_user, log_id } = functionData;

    // Store impersonation state for audit logging
    const impersonationState: ImpersonationState = {
      isImpersonating: true,
      originalAdminId: adminSession.user.id,
      impersonatedUserId: impersonated_user.id,
      impersonatedUserName: impersonated_user.full_name,
      logId: log_id,
    };
    
    // Store state in sessionStorage so it's tracked
    safeSetSessionStorage(IMPERSONATION_KEY, JSON.stringify(impersonationState));
    safeSetSessionStorage(ADMIN_SESSION_KEY, JSON.stringify(adminSession));

    // Copy the magic link to clipboard
    try {
      await navigator.clipboard.writeText(magic_link);
      alert(
        `✅ Login link copied to clipboard!\n\n` +
        `Impersonating: ${impersonated_user.full_name}\n\n` +
        `Paste this link into an incognito/private window to view as this user.\n\n` +
        `The link expires in 60 seconds.`
      );
    } catch (clipboardError) {
      // Fallback if clipboard API fails
      console.error('Clipboard error:', clipboardError);
      // Show the link in a prompt so user can copy manually
      prompt(
        `Copy this link and paste it into an incognito window:\n\n` +
        `Impersonating: ${impersonated_user.full_name}`,
        magic_link
      );
    }
  } catch (error) {
    console.error('Error starting impersonation:', error);
    throw error;
  }
};

/**
 * End the current impersonation session and restore admin session
 */
export const endImpersonation = async (): Promise<void> => {
  try {
    const impersonationState = getImpersonationState();
    if (!impersonationState) {
      throw new Error('No active impersonation session');
    }

    // Retrieve the stored admin session
    const storedAdminSession = safeGetSessionStorage(ADMIN_SESSION_KEY) || (typeof localStorage !== 'undefined' ? localStorage.getItem(ADMIN_SESSION_KEY) : null);
    if (!storedAdminSession) {
      throw new Error('No stored admin session found');
    }

    const adminSession = JSON.parse(storedAdminSession);

    // Update the impersonation log to mark it as ended
    // We need to restore the admin session first to have permission
    const { error: setSessionError } = await supabase.auth.setSession({
      access_token: adminSession.access_token,
      refresh_token: adminSession.refresh_token,
    });

    if (setSessionError) {
      throw setSessionError;
    }

    // Update the impersonation log
    const { error: updateError } = await supabase
      .from('impersonation_logs')
      .update({ ended_at: new Date().toISOString() })
      .eq('id', impersonationState.logId);

    if (updateError) {
      console.error('Error updating impersonation log:', updateError);
      // Don't throw - we still want to restore the session
    }

    // Clear impersonation state from both storages
    safeRemoveSessionStorage(IMPERSONATION_KEY);
    safeRemoveSessionStorage(ADMIN_SESSION_KEY);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(IMPERSONATION_KEY);
      localStorage.removeItem(ADMIN_SESSION_KEY);
    }

    // Close this tab and return to admin
    window.close();
    
    // If window.close() didn't work (some browsers block it), redirect instead
    setTimeout(() => {
      window.location.href = '/admin';
    }, 100);
  } catch (error) {
    console.error('Error ending impersonation:', error);
    // Try to clean up even if there's an error
    safeRemoveSessionStorage(IMPERSONATION_KEY);
    safeRemoveSessionStorage(ADMIN_SESSION_KEY);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(IMPERSONATION_KEY);
      localStorage.removeItem(ADMIN_SESSION_KEY);
    }
    throw error;
  }
};

/**
 * Clear impersonation state (useful for logout)
 */
export const clearImpersonationState = (): void => {
  safeRemoveSessionStorage(IMPERSONATION_KEY);
  safeRemoveSessionStorage(ADMIN_SESSION_KEY);
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(IMPERSONATION_KEY);
    localStorage.removeItem(ADMIN_SESSION_KEY);
  }
};

